import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { disabled as disabledField, form, FormField } from '@angular/forms/signals';
import { MatDialog } from '@angular/material/dialog';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { ApiFinding, FindingStatus } from 'api';
import {
  FindingStateJustificationComponent,
  FindingStateJustificationData,
} from 'component/details/finding-state/justification/finding-state-justification.component';

@Component({
  selector: 'comp-finding-state',
  templateUrl: 'finding-state.component.html',
  styleUrls: ['finding-state.component.scss'],
  imports: [TranslateModule, FormField, MatOption, MatSelect, NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FindingStateComponent {
  private readonly dialog = inject(MatDialog);

  readonly finding = input.required<ApiFinding>();
  readonly disabled = input<boolean>(false);
  readonly findingChange = output<ApiFinding>();

  protected readonly statusOptions: readonly FindingStatus[] = Object.values(FindingStatus);

  protected readonly statusModel = signal<{ status: FindingStatus }>({
    status: FindingStatus.Unresolved,
  });

  protected readonly statusForm = form(this.statusModel, (path) => {
    disabledField(path.status, {
      when: () => this.disabled(),
    });
  });

  protected readonly statusClass = computed(() => `status-${this.statusForm.status().value()}`);

  constructor() {
    effect(() => {
      this.statusModel.set({ status: this.finding().status });
    });
  }

  protected onStatusChange(): void {
    const finding = this.finding();
    const newStatus = this.statusForm.status().value();
    const previousStatus = finding.status;

    if (!newStatus || newStatus === previousStatus || !finding.id) {
      return;
    }

    if (!this.needsJustification(previousStatus, newStatus)) {
      this.patchFinding(previousStatus, newStatus);
      return;
    }

    this.openJustificationDialog(previousStatus, newStatus, finding.statusJustification);
  }

  private openJustificationDialog(
    previousStatus: FindingStatus,
    newStatus: FindingStatus,
    previousStatusJustification?: string,
  ): void {
    const dialogRef = this.dialog.open<
      FindingStateJustificationComponent,
      FindingStateJustificationData,
      FindingStateJustificationData | undefined
    >(FindingStateJustificationComponent, {
      data: {
        justification: previousStatusJustification,
        newStatus,
        previousStatus,
      },
    });

    dialogRef.afterClosed().subscribe((justificationData) => {
      if (!justificationData) {
        this.resetStatus(previousStatus);
        return;
      }

      this.patchFinding(previousStatus, newStatus, justificationData.justification);
    });
  }

  private patchFinding(
    previousStatus: FindingStatus,
    newStatus: FindingStatus,
    statusJustification?: string,
  ): void {
    if (this.needsJustification(previousStatus, newStatus) && !statusJustification?.trim()) {
      this.resetStatus(previousStatus);
      console.error('Could not patch finding status. Status must have a justification');
      return;
    }

    this.findingChange.emit({
      ...this.finding(),
      status: newStatus,
      statusJustification,
    });
  }

  private resetStatus(status: FindingStatus): void {
    this.statusModel.set({ status });
  }

  private needsJustification(previousStatus: FindingStatus, newStatus: FindingStatus): boolean {
    // According to EMR this is the only state transition where no justification is needed.
    return !(
      newStatus === FindingStatus.Unresolved && previousStatus === FindingStatus.NotApplicable
    );
  }
}
